/**
 * Every date, number and duration the interface prints goes through here.
 * dayjs is configured once, in this file. Nobody else imports a dayjs plugin.
 *
 * ── Being migrated ──────────────────────────────────────────────────────────
 * The formatting itself now lives in `lib/i18n/format.ts`, bound to the *active locale*
 * rather than to a module-level default — the defect that had an English interface printing
 * `2,2 Mln` and `633,80 €`. Every function below is a thin delegate kept so the seventy-odd
 * files importing this module keep working unchanged.
 *
 * New code should import from `lib/i18n` instead:
 *
 *   const f = useFormat()
 *   f.date(post.publishAt, { timeZone })   f.compact(n)   f.currencyCents(cents)
 *
 * The dayjs helpers here are *not* deprecated: `today`, `inZone`, `toIsoDate`,
 * `toCompactDate`, `eachDay`, `previousRange` and friends are calendar arithmetic and wire
 * formats, not user-visible copy, and they stay.
 */

import dayjs from 'dayjs'
import type { Dayjs, ConfigType } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import relativeTime from 'dayjs/plugin/relativeTime'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isoWeek from 'dayjs/plugin/isoWeek'
import duration from 'dayjs/plugin/duration'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'

import type { MetricUnit } from '@anubis-connector/shared'
import { formatDelta, formatMetric } from '@anubis-connector/shared'

import { adoptSessionLocale, getLocale } from './i18n/locale'
import { intlLocaleOf } from './i18n/shared'
import {
  formatBytes as i18nBytes,
  formatCompactNumber as i18nCompact,
  formatDate as i18nDate,
  formatDateRange as i18nDateRange,
  formatDateShort as i18nDateShort,
  formatDateTime as i18nDateTime,
  formatDecimal as i18nDecimal,
  formatDuration as i18nDuration,
  formatInteger as i18nInteger,
  formatMonthYear as i18nMonthYear,
  formatPercent as i18nPercent,
  formatRelativeCompact as i18nRelativeCompact,
  formatRelativeTime as i18nRelative,
  formatSigned as i18nSigned,
  formatTime as i18nTime,
  formatWeekday as i18nWeekday,
} from './i18n/format'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)
dayjs.extend(advancedFormat)
dayjs.extend(customParseFormat)
dayjs.extend(isoWeek)
dayjs.extend(duration)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

export { dayjs }
export type { Dayjs }

/** Re-exported so a component never reaches past this module for number formatting. */
export { formatMetric, formatDelta }

/* ── locale ───────────────────────────────────────────────────────────────── */

/**
 * AuthProvider calls this with `user.locale` once the session lands — which is layer 1 of
 * docs/I18N.md, the database row, arriving. It now feeds the shared locale store, so the
 * session's language reaches `useT()`, `useFormat()` and `<html lang>` in the same move.
 * An unrecognised value leaves the current choice alone.
 */
export const setActiveLocale = (locale: string): void => {
  adoptSessionLocale(locale)
}

/** The BCP-47 tag to hand to an `Intl` constructor — `en-GB` / `it-IT`. */
export const getActiveLocale = (): string => intlLocaleOf(getLocale())

/* ── dates ────────────────────────────────────────────────────────────────── */

/** ISO calendar date, `YYYY-MM-DD` — the form every range control speaks. */
export type IsoDate = string

export const today = (tz?: string): Dayjs => (tz ? dayjs().tz(tz) : dayjs())

export const inZone = (value: ConfigType, tz?: string): Dayjs =>
  tz ? dayjs(value).tz(tz) : dayjs(value)

export const toIsoDate = (value: ConfigType, tz?: string): IsoDate =>
  inZone(value, tz).format('YYYY-MM-DD')

/** Upstream `/stats/*` date form. See docs/API_CONTRACT.md rule 6 — do not normalise. */
export const toCompactDate = (value: ConfigType, tz?: string): string =>
  inZone(value, tz).format('YYYYMMDD')

/** Upstream `/v2/*` date-time form. */
export const toUpstreamDateTime = (value: ConfigType, tz?: string): string =>
  inZone(value, tz).format('YYYY-MM-DDTHH:mm:ss')

/* Everything from here to the end of the numbers section delegates to `lib/i18n/format.ts`,
   which is bound to the active locale. dayjs parses the input (it accepts far more shapes
   than `Date`), `Intl` writes the output. */

const zone = (tz?: string): { timeZone?: string } => (tz === undefined ? {} : { timeZone: tz })

/** Whatever dayjs accepts → an instant, so the Intl layer takes one shape only. */
const instant = (value: ConfigType): Date => dayjs(value).toDate()

export const formatDate = (value: ConfigType, tz?: string): string =>
  i18nDate(getLocale(), instant(value), zone(tz))

export const formatDateShort = (value: ConfigType, tz?: string): string =>
  i18nDateShort(getLocale(), instant(value), zone(tz))

export const formatDateTime = (value: ConfigType, tz?: string): string =>
  i18nDateTime(getLocale(), instant(value), zone(tz))

export const formatTime = (value: ConfigType, tz?: string): string =>
  i18nTime(getLocale(), instant(value), zone(tz))

export const formatWeekday = (value: ConfigType, tz?: string): string =>
  i18nWeekday(getLocale(), instant(value), 'short', zone(tz))

export const formatMonth = (value: ConfigType, tz?: string): string =>
  i18nMonthYear(getLocale(), instant(value), zone(tz))

/** `1 – 30 Sep 2026`, collapsing whatever the two ends share. */
export const formatDateRange = (from: ConfigType, to: ConfigType, tz?: string): string =>
  i18nDateRange(getLocale(), instant(from), instant(to), zone(tz))

/** `4 minutes ago`, `in 2 days` — and `yesterday` where the locale has a word for it. */
export const formatRelative = (value: ConfigType, base?: ConfigType): string =>
  i18nRelative(getLocale(), instant(value), base === undefined ? new Date() : instant(base))

/** Compact relative form for dense rows: `4m`, `3h`, `2d`, `5w`. */
export const formatRelativeCompact = (value: ConfigType, base?: ConfigType): string =>
  i18nRelativeCompact(getLocale(), instant(value), base === undefined ? new Date() : instant(base))

export const daysBetween = (from: ConfigType, to: ConfigType): number =>
  Math.abs(dayjs(to).startOf('day').diff(dayjs(from).startOf('day'), 'day')) + 1

/** Every calendar day in `[from, to]`, inclusive. */
export const eachDay = (from: ConfigType, to: ConfigType): IsoDate[] => {
  const out: IsoDate[] = []
  let cursor = dayjs(from).startOf('day')
  const end = dayjs(to).startOf('day')
  while (cursor.isSameOrBefore(end, 'day')) {
    out.push(cursor.format('YYYY-MM-DD'))
    cursor = cursor.add(1, 'day')
  }
  return out
}

/** The range immediately before `[from, to]`, same length — what a delta compares against. */
export const previousRange = (from: IsoDate, to: IsoDate): { from: IsoDate; to: IsoDate } => {
  const span = daysBetween(from, to)
  const end = dayjs(from).subtract(1, 'day')
  return { from: end.subtract(span - 1, 'day').format('YYYY-MM-DD'), to: end.format('YYYY-MM-DD') }
}

/** Offset label for a brand's timezone, e.g. `UTC+02:00`. */
export const zoneOffsetLabel = (tz: string): string => {
  const minutes = dayjs().tz(tz).utcOffset()
  const sign = minutes < 0 ? '-' : '+'
  const abs = Math.abs(minutes)
  const hh = String(Math.floor(abs / 60)).padStart(2, '0')
  const mm = String(abs % 60).padStart(2, '0')
  return `UTC${sign}${hh}:${mm}`
}

/** Fraction of the day elapsed in a timezone, 0–1. Drives the Now Rail marker. */
export const dayProgress = (tz?: string): number => {
  const now = today(tz)
  return (now.hour() * 3600 + now.minute() * 60 + now.second()) / 86_400
}

/** The browser's own zone, used as the fallback when no brand is active. */
export const localTimezone = (): string => dayjs.tz.guess()

/* ── numbers ──────────────────────────────────────────────────────────────── */

export const formatInt = (value: number): string => i18nInteger(getLocale(), value)

export const formatDecimal = (value: number, digits = 1): string =>
  i18nDecimal(getLocale(), value, digits)

/** `12.4K`, `3.1M` — for axis ticks and dense tiles. */
export const formatCompact = (value: number): string => i18nCompact(getLocale(), value)

/** Takes a percentage, not a fraction: `formatPercent(12.4)` → `12.4%` / `12,4%`. */
export const formatPercent = (value: number, digits = 1): string =>
  i18nPercent(getLocale(), value, digits)

export const formatSigned = (value: number, digits = 0): string =>
  i18nSigned(getLocale(), value, digits)

/** Metric value in the session's locale, rather than the shared default. */
export const formatMetricValue = (value: number, unit: MetricUnit): string =>
  formatMetric(value, unit, getActiveLocale())

/** Minutes → `3h 12m` / `3 h 12 min`. */
export const formatDuration = (minutes: number): string => i18nDuration(getLocale(), minutes)

export const formatBytes = (bytes: number): string => i18nBytes(getLocale(), bytes)

/** `00`–`23`, the Now Rail scale. */
export const hourLabel = (hour: number): string => String(hour).padStart(2, '0')

/* ── the locale-bound module, re-exported ─────────────────────────────────────
   Everything `lib/i18n/format.ts` adds and this module never had. Import either path while
   the migration runs; `lib/i18n` is where it all ends up. */

export {
  formatCurrency,
  formatCurrencyCents,
  formatDateLong,
  formatDateNumeric,
  formatDurationSeconds,
  formatList,
  formatNumber,
  formattersFor,
  monthNames,
  useFormat,
  weekdayNames,
} from './i18n/format'
export type { DateInput, DurationStyle, Formatters, NameWidth, ZoneOption } from './i18n/format'
export type { Locale } from './i18n/shared'

/** Initials for an avatar fallback, at most two letters. */
export const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?'
